import React, { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useI18n } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function Partnership() {
  const { t } = useI18n();
  const p = t.partner;
  const [form, setForm] = useState({ org_type: "", company: "", name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_type) { toast.error("—"); return; }
    setLoading(true);
    try {
      await api.post("/partnerships", form);
      toast.success(p.success);
      setForm({ org_type: "", company: "", name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const fieldCls = "rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent";

  return (
    <section id="partner" className="bg-secondary py-24 md:py-32 border-t border-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="overline text-accent">{p.overline}</p>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[1.02]">{p.title}</h2>
          <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed max-w-lg">{p.body}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="partner-form">
            <div className="flex flex-col gap-2">
              <Label className="overline text-muted-foreground">{p.orgType}</Label>
              <Select value={form.org_type} onValueChange={set("org_type")}>
                <SelectTrigger data-testid="partner-orgtype" className={fieldCls}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{p.orgOpts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <PField label={p.company}><Input required data-testid="partner-company" value={form.company} onChange={(e) => set("company")(e.target.value)} className={fieldCls} /></PField>
            <PField label={p.name}><Input required data-testid="partner-name" value={form.name} onChange={(e) => set("name")(e.target.value)} className={fieldCls} /></PField>
            <PField label={p.email}><Input type="email" required data-testid="partner-email" value={form.email} onChange={(e) => set("email")(e.target.value)} className={fieldCls} /></PField>
            <PField label={p.phone}><Input data-testid="partner-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className={fieldCls} /></PField>
            <div className="sm:col-span-2">
              <PField label={p.message}><Textarea rows={2} data-testid="partner-message" value={form.message} onChange={(e) => set("message")(e.target.value)} className={fieldCls + " resize-none"} /></PField>
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={loading} data-testid="partner-submit"
                className="rounded-full bg-pine px-10 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-cream transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60">
                {p.submit}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function PField({ label, children }) {
  return <div className="flex flex-col gap-2"><Label className="overline text-muted-foreground">{label}</Label>{children}</div>;
}
